'use strict';

/**
 * Site class contains general purpose site-specific features.
 *
 * @version 1.0.0
 */
class Site {

	/**
	 * Class constructor
	 *
	 * @param {Object} options Options for the class.
	 */
	constructor(options = {}) {
		this.options = {};

		// Init polyfills
		const polyfills = new Polyfills();
		polyfills.init();

		// Init off-canvas after mobile menu is ready
		document.addEventListener('mobilemenu-init-done', () => {
			const offCanvas = new OffCanvas({
				debug: false,
				overlay: true
			});
		});

		// Init mobile menu
		const mobileMenu = new MobileMenu();
		mobileMenu.init();

		// Initialize
		this.init(options);
	}

	/**
	 * Init the class by calling applicable init methods
	 *
	 * @param {Object} options Options for the class.
	 * @return {Object}
	 */
	init(options = {}) {
		// Merge user options to the defaults
		this.options = {
			responsiveTables: {
				selector: 'main table'
			},
			imageLinks: {
				parentSelector: 'main'
			},
			...options
		};

		// Call individual init methods
		this.initResponsiveTables();
		this.initSkipLinks();
		this.initImageLinks();
		this.initTopMenu();
		this.initAutoComplete();
		this.initMemberLogin();
		this.initMediaUpload();

		// Dispatch custom event when init is done
		document.dispatchEvent(
			new CustomEvent('site-init-done', {
				bubbles: true,
				cancelable: true
			})
		);

		return this;
	}

	/**
	 * Initialize responsive tables
	 *
	 * Finds content tables and wraps them with div.table-wrapper.
	 */
	initResponsiveTables() {
		document.querySelectorAll(this.options.responsiveTables.selector).forEach(table => {
			if (!table.closest('.table-wrapper')) {
				const tableWrapper = document.createElement('div');
				tableWrapper.classList.add('table-wrapper');
				tableWrapper.classList.add('overflow-x-auto');
				table.parentNode.insertBefore(tableWrapper, table);
				tableWrapper.appendChild(table);
			}
		});
	}

	/**
	 * Initialize skip links
	 *
	 * Finds skip links and enhances their behaviour for various screen readers and mobile devices.
	 */
	initSkipLinks() {
		const skipLinks = document.querySelectorAll('.skip-link:not([data-skip-link])');
		if (skipLinks.length) {
			const skipToBlur = event => {
				if (event.target.getAttribute('data-tabindex')) {
					event.target.removeAttribute('tabindex');
					event.target.removeAttribute('data-tabindex');
				}
			};
			skipLinks.forEach(skipLink => {
				skipLink.setAttribute('data-skip-link', true);
				skipLink.addEventListener('click', event => {
					const skipTo = document.getElementById(event.target.href.split('#')[1]);
					if (skipTo && skipTo.getAttribute('tabindex') != '-1') {
						event.preventDefault();
						skipTo.setAttribute('tabindex', '-1');
						skipTo.setAttribute('data-tabindex', true);
						skipTo.addEventListener('blur', skipToBlur);
						skipTo.addEventListener('focusout', skipToBlur);
						skipTo.focus();
					}
				});
			});
		}
	}

	/**
	 * Initialize image links
	 */
	initImageLinks() {

		// Parent of image links
		let parentNode = document.querySelector(this.options.imageLinks.parentSelector);
		if (!parentNode) return;

		// Add glightbox class to image links
		parentNode.querySelectorAll('a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".gif"]').forEach(link => {
			if (!link.classList.contains('glightbox')) {
				if (!link.getAttribute('data-title') && !link.getAttribute('data-glightbox')) {
					let figcaption = link.parentNode.querySelector('figcaption');
					if (figcaption) {
						let caption = figcaption ? figcaption.textContent : '';
						link.setAttribute('data-title', caption);
					}
				}
				link.classList.add('glightbox');
			}
		});

		// Initialize GLightbox
		if (parentNode.querySelector('.glightbox')) {
			GLightbox();
		}
	}

	/**
	 * Initialize top menu
	 *
	 * This method makes sure that a) we don't accidentally open multiple dropdowns at once (one with
	 * focus, another with hover) and b) dropdown is most likely opened intentionally (slight delay).
	 */
	initTopMenu() {
		const rootLinks = document.querySelectorAll('.top-menu__item--level-1--has-children');
		if (!rootLinks || !rootLinks.length) return;

		rootLinks.forEach(link => {
			link.addEventListener('mouseover', event => {
				if (event.target !== link) return;
				const focusLink = document.querySelector('.top-menu__item:focus');
				if (focusLink && focusLink !== link) {
					focusLink.blur();
				}
				link.parentNode.classList.add('js-hover-delay');
				link.hoverDelayTimeout = window.setTimeout(() => {
					link.parentNode.classList.remove('js-hover-delay');
				}, 500);
			});
			link.addEventListener('mouseout', () => {
				if (Object.prototype.hasOwnProperty.call(link, 'hoverDelayTimeout')) {
					link.classList.remove('js-hover-delay');
					window.clearTimeout(link.hoverDelayTimeout);
				}
			});
		});
	}

	/**
	 * Initialize autocomplete club search
	 */
	initAutoComplete() {
		const searchInput = document.getElementById("js-club-search-input");
		if (!searchInput) return;

		const searchForm = document.getElementById('js-club-search-form');

		searchInput.addEventListener("keydown", event => {
			if (event.key === "Enter") {
				searchForm.submit();
			}
		});

		const autoCompleteJS = new autoComplete({
			name: searchInput.getAttribute('data-name'),
			selector: "#js-club-search-input",
			data: {
				src: async function () {

					// "Loading" placeholder text
					searchInput.setAttribute("placeholder", searchInput.getAttribute('data-loading'));

					// Fetch External Data
					const source = await fetch(searchForm.getAttribute('action') + 'cities.json');
					const data = await source.json();

					// Restore default placeholder text
					searchInput.setAttribute("placeholder", searchInput.getAttribute('data-placeholder'));

					return data;
				},
				key: [
					"title"
				],
				results: list => {
					// Filter duplicates
					const filteredResults = Array.from(
						new Set(list.map(value => value.match))
					).map(food => {
						return list.find(value => value.match === food);
					});
					return filteredResults;
				},
				cache: true
			},
			trigger: {
				event: [
					"input",
					"focus"
				]
			},
			resultsList: {
				idName: "autoComplete__list",
				className: "autoComplete__list"
			},
			diacritics: false,
			placeHolder: searchInput.getAttribute('data-placeholder'),
			searchEngine: "strict",
			highlight: true,
			maxResults: 10,
			threshold: 2,
			debounce: 150,
			resultItem: {
				content: (data, element) => {

					// Prepare Value's Key
					const key = Object.keys(data.value).find(key => data.value[key] === element.innerText);

					// Modify Results Item
					element.style = "display: flex; justify-content: space-between;";
					element.innerHTML = '<span style="text-overflow: ellipsis; white-space: nowrap; overflow: hidden;">'
						+ element.innerHTML
						+ '</span>';
				},
				idName: "autoComplete__result",
				className: "autoComplete__result"
			},
			noResults: (dataFeedback, generateList) => {

				// Generate autoComplete List
				generateList(autoCompleteJS, dataFeedback, dataFeedback.results);

				// "No Results" List Item
				const result = document.createElement("li");
				result.setAttribute("class", "no_result");
				result.setAttribute("tabindex", "1");
				result.innerHTML = '<span style="display: flex; align-items: center; font-weight: 100; color: rgba(0,0,0,.2);">'
					+ searchInput.getAttribute('data-no-results').replace('%s', dataFeedback.query)
					+ '</span>';
				document
					.querySelector(`#${autoCompleteJS.resultsList.idName}`)
					.appendChild(result);
			},
			onSelection: (feedback) => {
				searchInput.blur();
				const selection = feedback.selection.value[feedback.selection.key];
				searchInput.value = selection;
				searchForm.submit();
			}
		});
	}

	/**
	 * Initialize member login
	 */
	initMemberLogin() {
		const loginHash = window.location.hash;
		if (loginHash && loginHash.match(/^\#member-login-[0-9]*$/)) {
			const loginPageID = loginHash.match(/[0-9]+/);
			if (loginPageID) {
				const loginFormContainer = document.querySelector('#member-login');
				const loginForm = loginFormContainer.querySelector('form');
				const pageIDInput = document.createElement('input');
				pageIDInput.setAttribute('type', 'hidden');
				pageIDInput.setAttribute('name', 'page_id');
				pageIDInput.setAttribute('value', parseInt(loginPageID[0]));
				loginForm.appendChild(pageIDInput);
				loginFormContainer.scrollIntoView();
				const scrollTop = loginFormContainer.getBoundingClientRect().top + window.pageYOffset - document.querySelector('.top-menu').offsetHeight;
				window.scrollTo(0, scrollTop);
				loginForm.setAttribute('tabindex', '-1');
				loginForm.classList.add('outline-none');
				loginForm.focus();
			}
		}
	}

	/**
	 * Initialize media upload
	 */
	initMediaUpload() {

		// get media upload form and bail out early if none found
		const mediaUploadForm = document.querySelector('#FormBuilder_media-upload');
		if (!mediaUploadForm) return;

		// get media upload container and button; container is hidden by default and can be shown by
		// clicking the button - but if the button is not present, the container is shown by default
		const mediaUploadContainer = document.querySelector('.js-media-upload');
		if (mediaUploadContainer) {
			const mediaUploadButton = document.querySelector('.js-media-upload-button');
			if (mediaUploadButton) {
				mediaUploadButton.addEventListener('click', () => {
					mediaUploadContainer.removeAttribute('hidden');
					mediaUploadButton.remove();
				});
			} else {
				mediaUploadContainer.removeAttribute('hidden');
			}
		}

		// loop through file upload fields and hide all but the first one; add a button to show more
		let fileUploadNum = 0;
		mediaUploadForm.querySelectorAll('.InputfieldFormBuilderFileUpload').forEach(fileUpload => {

			++fileUploadNum;

			fileUpload.setAttribute('data-file-upload-num', fileUploadNum);

			if (fileUploadNum === 1) {
				// display title and summary fields for the first file upload field
				mediaUploadForm.querySelectorAll('[class^="field field--media_' + fileUploadNum + '_"]').forEach(fileUploadTextField => {
					fileUpload.insertAdjacentElement('beforeend', fileUploadTextField);
					fileUploadTextField.classList.add('media-upload__custom-field');
				});
			}

			if (fileUploadNum > 1) {
				fileUpload.setAttribute('hidden', true);
			}

			// populate preview image in InputfieldFormBuilderFilePreviewImage
			// note: technically this should be handled by FormBuilder, but for some reason it doesn't seem
			// to work, so we're doing it here manually, which also gives us more control over the behaviour
			fileUpload.addEventListener('change', event => {

				// ignore change events from custom fields
				if (event.target.closest('.media-upload__custom-field')) {
					return;
				}

				const previewImage = fileUpload.querySelector('.InputfieldFormBuilderFilePreviewImage img');
				if (previewImage) {

					// if empty, clear preview image and remove remove button
					if (!event.target.files || !event.target.files.length) {

						// clear preview image and remove the remove button
						previewImage.src = '';
						const removeButton = previewImage.nextElementSibling;
						if (removeButton) {
							removeButton.remove();
						}

						// if this is not the first file upload field, hide it
						if (fileUpload.getAttribute('data-file-upload-num') > 1) {
							fileUpload.setAttribute('hidden', true);
						}

						// if add media button is hidden and there are fewer than 5 file upload fields, show it
						const addMediaButton = document.querySelector('.js-media-upload-add-media-button[hidden]');
						if (addMediaButton && document.querySelectorAll('.InputfieldFormBuilderFileUpload:not([hidden])').length < 5) {
							addMediaButton.removeAttribute('hidden');
						}

						return;
					}

					// if not empty, show preview image and add remove button
					const file = event.target.files[0];
					const reader = new FileReader();
					reader.onload = e => {
						previewImage.src = e.target.result;
					};
					reader.readAsDataURL(file);
					const removeButton = document.createElement('button');
					removeButton.setAttribute('type', 'button');
					removeButton.classList.add('button');
					removeButton.classList.add('button--gray');
					removeButton.setAttribute('aria-label', mediaUploadContainer.getAttribute('data-remove-media-button-text'));
					const removeButtonIcon = document.createElement('i');
					removeButtonIcon.classList.add('icon');
					removeButtonIcon.classList.add('icon-cancel');
					removeButton.appendChild(removeButtonIcon);
					removeButton.addEventListener('click', () => {
						fileUpload.querySelector('input[type="file"]').value = '';
						fileUpload.dispatchEvent(new Event('change'));
					});
					previewImage.insertAdjacentElement('afterend', removeButton);
				}
			});
		});

		// add button to show more file upload fields
		const addMediaButton = document.createElement('button');
		addMediaButton.setAttribute('type', 'button');
		addMediaButton.classList.add('button');
		addMediaButton.classList.add('button--blue');
		addMediaButton.classList.add('js-media-upload-add-media-button');
		addMediaButton.innerText = mediaUploadContainer.getAttribute('data-add-media-button-text');
		addMediaButton.addEventListener('click', () => {
			const nextfileUpload = mediaUploadForm.querySelector('.InputfieldFormBuilderFileUpload[hidden]');
			if (nextfileUpload) {

				// show next file upload field and hide add media button if there are no more hidden file upload fields
				nextfileUpload.removeAttribute('hidden');
				if (!mediaUploadForm.querySelector('.InputfieldFormBuilderFileUpload[hidden]')) {
					addMediaButton.setAttribute('hidden', true);
				}

				// show title and summary fields for the next file upload field
				mediaUploadForm.querySelectorAll('[class^="field field--media_' + nextfileUpload.getAttribute('data-file-upload-num') + '_"]').forEach(fileUploadTextField => {
					nextfileUpload.insertAdjacentElement('beforeend', fileUploadTextField);
					fileUploadTextField.classList.add('media-upload__custom-field');
				});

				// automatically click on the file input to trigger file selection dialog
				nextfileUpload.querySelector('input[type="file"]').click();
			}
		});
		mediaUploadForm.querySelector('.InputfieldFormBuilderFileList').insertAdjacentElement('afterend', addMediaButton);
	}
}

window._site = new Site();
